
import {defineComponent} from 'vue';
import Slider from "@/components/rechner/Slider.vue";
import SliderTO from "@/types/SliderTO";
import {CalcTrigger} from "@/types/CalcTrigger";
import EHinweis from "@/enums/EHinweis";
import HinweisTO from "@/types/HinweisTO";
import Info from "@/components/rechner/Info.vue";
import {EUR} from "@/utils/NumberUtil";

export default defineComponent({
    name: 'RechnerFinBestaetigung',
    components: {Info, Slider},
    data() {
        return {
            minDarlehen: 0,
            isVolltilgung: false,
            showErrors: false,
            showEigenkapitalError: false,
            //Werden nur 1x aus dem store gelesen, die Manipulation erfolgt direkt hier, also nicht über den store,
            //da die Antwort sonst zu lange brauch, was zu unerwünschtem Verhalten der Slider führt
            sliderEigenkapital: this.$store.state.sliderEigenkapital as SliderTO,
            sliderAusgaben: this.$store.state.sliderAusgaben as SliderTO,

            textSliderAusgaben: "",
            textToolTipAusgaben: "",
            textHinweisAusgaben: "",
            textSliderKaufpreis: "",
            textToolTipKaufpreis: "",
            textSliderEigenkapital: "",
            textToolTipEigenkapital: "",
            textHinweisEigenkapital: "",
            textSliderEinnahmen: "",
            textToolTipEinnahmen: "",
            textHinweisEinnahmen: "",
            textHinweisFinanzierungNichtMoeglich: ""
        };
    },
    props: {
        showHinweisRate: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        //Schreiben der Werte in den Store
        onKaufpreisChanged(value: number): void {
            this.hideErrors();
            this.updateSliderEigenkapital(value);
            this.$store.commit(CalcTrigger.KAUFPREIS, value);
        },
        onEigenkapitalChanged(value: number): void {
            this.hideErrors();
            this.sliderEigenkapital.value = value;
            this.$store.commit(CalcTrigger.EIGENKAPITAL, value);
        },
        onEinnahmenChanged(value: number): void {
            this.hideErrors();
            this.updateSliderAusgaben(value);
            this.$store.commit(CalcTrigger.EINNAHMEN, value);
        },
        onAusgabenChanged(value: number): void {
            this.hideErrors();
            this.sliderAusgaben.value = value;
            this.$store.commit(CalcTrigger.AUSGABEN, value);
        },
        hideErrors(): void {
            this.sliderEinnahmen.hinweis = {} as HinweisTO;
            this.sliderAusgaben.hinweis = {} as HinweisTO;
            this.sliderPreis.hinweis = {} as HinweisTO;
            this.sliderEigenkapital.hinweis = {} as HinweisTO;
            this.sliderRate.hinweis = {} as HinweisTO;
        },
        updateSliderEigenkapital(kaufpreis: number): void {
            let maxEigenkapital = kaufpreis - this.minDarlehen;

            this.sliderEigenkapital.max = maxEigenkapital;

            if (this.sliderEigenkapital.value > maxEigenkapital) {
                this.sliderEigenkapital.value = maxEigenkapital;
                this.onEigenkapitalOutOfRange();
            }
        },
        updateSliderAusgaben(einnahmen: number): void {
            if (einnahmen > 0) {
                this.sliderAusgaben.max = einnahmen;

                if (this.sliderAusgaben.value > einnahmen) {
                    this.sliderAusgaben.value = einnahmen;
                }
            }
        },
        onEigenkapitalOutOfRange(): void {
            this.sliderEigenkapital.hinweis = {
                hinweisID: EHinweis.EIGENKAPITAL_REDUZIERT,
                text: this.textHinweisEigenkapital.replace("[[X]]", this.minDarlehen.toLocaleString() + " " + EUR)
            };
        },
    },
    computed:
        {
            //Lesen der Werte aus dem Store
            sliderPreis(): SliderTO {
                return this.$store.state.sliderPreis;
            },
            sliderEinnahmen(): SliderTO {
                return this.$store.state.sliderEinnahmen;
            },
            //Rate wird benötigt um auf den Hinweis, dass die Rate vom Backend angepasst wurde zu reagieren.
            sliderRate(): SliderTO {
                return this.$store.state.sliderRate;
            },
            maxBeleihung(): number {
                let sollzinsbindungen = this.$store.state.sollzinsbindungen;
                let aktiveSollzinsbindung = this.$store.state.selectedSollzinsbindung;
                let maxBeleihung = 100;

                for (let sollzinsbindung of sollzinsbindungen) {
                    if (sollzinsbindung.dauer == aktiveSollzinsbindung) {
                        maxBeleihung = sollzinsbindung.maxBeleihungProzent;
                    }
                }
                return maxBeleihung;
            },
            isInitDataLoaded(): boolean {
                return this.$store.state.initDataLoaded;
            },
            hinweisRate(): string {
                if (this.sliderRate.hinweis && this.sliderRate.hinweis.hinweisID == EHinweis.MONATLICHE_RATE_WURDE_ERHOEHT) {
                    return this.textHinweisFinanzierungNichtMoeglich.replace("[[X]]", this.sliderRate.min.toLocaleString());
                } else {
                    return "";
                }
            },
        },
    watch: {
        isInitDataLoaded(isLoaded: boolean): void {
            if (isLoaded) {
                this.sliderAusgaben = this.$store.state.sliderAusgaben;
                this.updateSliderAusgaben(this.sliderEinnahmen.value);

                this.minDarlehen = this.$store.state.sliderDarlehen.min;
                this.sliderEigenkapital = this.$store.state.sliderEigenkapital;
                this.updateSliderEigenkapital(this.sliderPreis.value);

                this.textSliderAusgaben = this.$store.state.fachtexte["rechner.berechnung.slider.ausgaben"];
                this.textToolTipAusgaben = this.$store.state.fachtexte["rechner.berechnung.tooltip.ausgaben"];
                this.textHinweisAusgaben = this.$store.state.fachtexte["rechner.berechnung.hinweis.eingabefeld.ausgaben"];
                this.textSliderKaufpreis = this.$store.state.fachtexte["rechner.berechnung.slider.kaufpreis"];
                this.textToolTipKaufpreis = this.$store.state.fachtexte["rechner.berechnung.tooltip.slider.kaufpreis"];
                this.textSliderEigenkapital = this.$store.state.fachtexte["rechner.berechnung.slider.eigenkapital"];
                this.textToolTipEigenkapital = this.$store.state.fachtexte["rechner.berechnung.tooltip.slider.eigenkapital"];
                this.textHinweisEigenkapital = this.$store.state.fachtexte["rechner.berechnung.hinweis.eingabefeld.eigenkapital"];
                this.textSliderEinnahmen = this.$store.state.fachtexte["rechner.berechnung.slider.einnahmen"];
                this.textToolTipEinnahmen = this.$store.state.fachtexte["rechner.berechnung.tooltip.einnahmen"];
                this.textHinweisEinnahmen = this.$store.state.fachtexte["rechner.berechnung.hinweis.eingabefeld.einnahmen"];
                this.textHinweisFinanzierungNichtMoeglich = this.$store.state.fachtexte["rechner.berechnung.hinweis.finanzierung.nicht.moeglich"];
            }
        },
    },
});
